import { useEffect, useRef, useState } from 'react';

import { ChartTypeRegistry } from 'chart.js';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetReportingColumnsQuery } from 'apis/report-configuration.api';
import { useDistributeMutation, useGetTemplateByIdQuery, usePostExecuteQueryMutation } from 'apis/report-templates.api';
import {
  chartTypeMap,
  getDisplayTypeFromDataType,
  getTypeFromDataType,
  typeStringMap,
  VisualizationType,
} from 'constants/reports-templates';
import { ROUTES } from 'constants/routes';
import { useOpen } from 'hooks/useOpen';
import { CommonError } from 'interfaces/shared.interface';
import { ErrorModal, LoadingOverlay } from 'shared-components';
import { getRandomColor } from 'utils/global';

import CreateReportTemplateFiltersBlock, { ReportTemplateFiltersBlockHandle } from './CreateReportTemplateFiltersBlock';
import CreateReportTemplateHeader from './CreateReportTemplateHeader';
import CreateReportTemplateSchedule from './CreateReportTemplateSchedule';

const CreateReportTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { data: selectedColumnsData } = useGetReportingColumnsQuery();

  const [postExecuteQuery, { isLoading: isExecuteQueryLoading, error: executeError }] = usePostExecuteQueryMutation();

  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { data: templateData, isLoading: isTemplateLoading } = useGetTemplateByIdQuery<any>(id ?? '', {
    skip: !isEditMode,
  });

  const [postDistribute, { isLoading: isDistributeLoading, error: distributeError }] = useDistributeMutation();
  const filtersBlockRef = useRef<ReportTemplateFiltersBlockHandle>(null);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(false);
  const [chartData, setChartData] = useState<{ type: keyof ChartTypeRegistry; data: any } | null>(null);
  const [visualizationType, setVisualizationType] = useState<string | undefined>();
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>();
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const errorData = (executeError as CommonError) || (distributeError as CommonError);
  const errorDetails = errorData?.data?.error as { message?: string; details?: string };

  const [isOpenErrorModal, handleOpenErrorModal, handleCloseErrorModal] = useOpen();

  const handleReset = () => {
    filtersBlockRef.current?.resetForm();
    setChartData(null);
    setVisualizationType(undefined);
  };

  const visualizationTypeMap: Record<number, VisualizationType> = {
    1: 'Table',
    2: 'Bar',
    3: 'Line',
    4: 'Pie',
    5: 'Doughnut',
    6: 'Scatter',
    7: 'Polar Area',
    8: 'Radar',
  };

  const handleDiscard = () => {
    navigate(ROUTES.reportTemplates);
  };

  const handlePreviewClick = async () => {
    if (!filtersBlockRef.current) {
      return;
    }

    const config = filtersBlockRef.current.getCurrentConfiguration();
    if (!config) {
      return;
    }

    try {
      const { visualization, tableName, joins, filters } = config;

      const mappedJoins = (joins ?? [])
        .map((join: any) => {
          const leftCol = selectedColumnsData?.result.find(
            (col: any) => col.name === join.leftColumn && col.tableName === join.leftTable
          );
          const rightCol = selectedColumnsData?.result.find(
            (col: any) => col.name === join.rightColumn && col.tableName === join.rightTable
          );

          if (!leftCol || !rightCol) {
            return null;
          }

          const getColumnMeta = (col: any) => ({
            name: col.name,
            tableName: col.tableName,
            type: col.columnType,
            displayName: col.name,
            displayType: typeStringMap[col.columnType],
          });

          return {
            id: join.id,
            leftTable: join.leftTable,
            rightTable: join.rightTable,
            leftColumn: join.leftColumn,
            rightColumn: join.rightColumn,
            column: getColumnMeta(leftCol),
            joinColumn: getColumnMeta(rightCol),
          };
        })
        .filter(Boolean);

      const normalizedFilters = (filters ?? [])
        .map((filter: any) => {
          const colMeta = selectedColumnsData?.result.find(
            (col: any) => col.name === filter.column && col.tableName === tableName
          );
          if (!colMeta) {
            return null;
          }

          const dataType = typeStringMap[colMeta.columnType];
          if (!dataType) {
            return null;
          }

          return {
            column: {
              name: colMeta.name,
              displayName: colMeta.name,
              type: getTypeFromDataType(dataType),
              displayType: getDisplayTypeFromDataType(dataType),
              tableName: colMeta.tableName,
            },
            condition: filter.condition,
            value: filter.value,
            isLocked: filter.locked,
          };
        })
        .filter(Boolean);

      const normalizedSorting = (config.sorting ?? [])
        .map((sortItem: any, index: number) => {
          const colMeta = selectedColumnsData?.result.find(
            (col: any) => col.name === sortItem.column && col.tableName === tableName
          );
          if (!colMeta) {
            return null;
          }

          const dataType = typeStringMap[colMeta.columnType];
          if (!dataType) {
            return null;
          }

          return {
            column: {
              name: colMeta.name,
              displayName: colMeta.name,
              type: getTypeFromDataType(dataType),
              displayType: getDisplayTypeFromDataType(dataType),
              tableName: colMeta.tableName,
            },
            direction: sortItem.direction === 'asc' ? 1 : 2,
            order: index + 1,
          };
        })
        .filter(Boolean);

      setVisualizationType(visualization?.type);
      setSelectedColumn(visualization?.column);

      if (visualization?.type !== 'Table') {
        let baseColumn;

        const aggregate = config.aggregates?.find((agg: any) => {
          const normalizedFunc = agg.func.endsWith(' of') ? agg.func.slice(0, -3) : agg.func;
          const fullName = `${normalizedFunc} of ${agg.column}`;
          return fullName.trim() === visualization.column.trim();
        });

        if (visualization.column === 'Count') {
          baseColumn = {
            name: 'Count',
            displayName: 'Count',
            type: 3,
            displayType: 'INT',
            tableName,
          };
        } else if (aggregate) {
          baseColumn = {
            name: aggregate.column,
            displayName: `${aggregate.func} of ${aggregate.column}`,
            type: 3,
            displayType: 'INT',
            tableName,
          };
        } else {
          console.warn('Trying to find in selectedColumnsData:', visualization.column);
          const columnMeta = selectedColumnsData?.result.find(
            (col: any) => col.name === visualization.column && col.tableName === tableName
          );
          if (!columnMeta) {
            return;
          }

          const rawType = columnMeta.columnType;
          if (rawType == null) {
            return;
          }

          const dataType = typeStringMap[rawType];
          if (!dataType) {
            return;
          }

          baseColumn = {
            name: columnMeta.name,
            displayName: columnMeta.name,
            type: getTypeFromDataType(dataType),
            displayType: getDisplayTypeFromDataType(dataType),
            tableName,
          };
        }

        const payload = {
          query: {
            tableName,
            joins: mappedJoins,
            sorting: normalizedSorting,
            filters: normalizedFilters,
            displayColumns: [baseColumn],
            ...(Array.isArray(config.groupBy) && config.groupBy.length === 1 ? { groupBy: config.groupBy[0] } : {}),
          },
          column: baseColumn,
          timelapseType: visualization.timelapseType ?? 2,
        };

        const response = await postDistribute(payload).unwrap();

        const preparedChartData = response?.result
          ? {
              labels: response.result.labels,
              datasets: [
                {
                  label: `${tableName}/${visualization.column}`,
                  data: response.result.values,
                  backgroundColor: response.result.values.map(() => getRandomColor(0.5)),
                  borderColor: 'rgba(0,0,0,0.1)',
                  borderWidth: 1,
                },
              ],
            }
          : null;

        setChartData({
          type: chartTypeMap[visualization.type],
          data: preparedChartData,
        });
      } else {
        const displayColumns = (visualization.columns ?? [])
          .map((colName: string) => {
            const colMeta = selectedColumnsData?.result.find(
              (col: any) => col.name === colName && col.tableName === tableName
            );

            if (!colMeta) {
              return null;
            }

            const rawType = colMeta.columnType;

            const dataType = typeStringMap[rawType];
            if (!dataType) {
              return null;
            }

            return {
              name: colMeta.name,
              displayName: `${colMeta.tableName} → ${colMeta.name}`,
              type: getTypeFromDataType(dataType),
              displayType: getDisplayTypeFromDataType(dataType),
              tableName: colMeta.tableName,
            };
          })
          .filter(Boolean);

        const response = await postExecuteQuery({
          query: {
            tableName,
            joins: mappedJoins,
            sorting: normalizedSorting,
            filters: normalizedFilters,
            displayColumns,
            rowLimit: visualization.rowLimit ?? 50,
          },
        }).unwrap();

        const tableColumns = displayColumns.map((col: any) => {
          const fieldName = `${col.tableName} -> ${col.name}`;

          return {
            field: fieldName,
            header: col.displayName || fieldName,
            name: col.name,
            tableName: col.tableName,
          };
        });

        tableColumns.forEach((col: any) => {
          const hasField = response.result[0] && response.result[0].hasOwnProperty(col.field);
          if (!hasField && response.result[0]) {
          }
        });

        setTableColumns(tableColumns);
        setTableData([...response.result]);
      }
    } catch (error) {
      handleOpenErrorModal();
    }
  };

  const initialVisualization = (() => {
    const result = templateData?.result;
    const typeId = result?.visualizationTypeId;
    const columnName = result?.distributionColumn?.name;

    if (!typeId || !columnName) {
      return null;
    }

    if (typeId === 1) {
      return {
        type: 'Table' as const,
        columns: [columnName],
        rowLimit: 50,
      };
    }

    return {
      type: visualizationTypeMap[typeId] as Exclude<VisualizationType, 'Table'>,
      column: columnName,
    };
  })();

  useEffect(() => {
    if (visualizationType === 'Table') {
    }
  }, [visualizationType, tableColumns, tableData]);

  useEffect(() => {
    if (templateData?.result) {
      setTimeout(() => {
        filtersBlockRef.current?.setInitialConfiguration(templateData.result);
      }, 0);
    }
  }, [templateData]);

  return (
    <div data-cy="report-templates-create-page" className="flex flex-col h-screen mx-[-40px] my-[-24px]">
      <ErrorModal
        errorMessage={errorDetails?.message}
        description={errorDetails?.details}
        isOpen={isOpenErrorModal}
        handleClose={handleCloseErrorModal}
      />

      <CreateReportTemplateHeader
        onReset={handleReset}
        onDiscard={handleDiscard}
        isPreviewEnabled={isPreviewEnabled}
        handlePreviewClick={handlePreviewClick}
        filtersBlockRef={filtersBlockRef}
        isEditMode={isEditMode}
        templateId={id}
      />

      <div className="flex h-full">
        <div className="flex-1 overflow-hidden w-full">
          <LoadingOverlay isLoading={isDistributeLoading || isExecuteQueryLoading} />
          <CreateReportTemplateSchedule
            chartJsType={chartData?.type ?? 'bar'}
            chartJsData={chartData?.data ?? { labels: [], datasets: [] }}
            visualizationType={visualizationType}
            columns={visualizationType === 'Table' ? tableColumns : selectedColumn ? [selectedColumn] : []}
            tableData={tableData}
          />
        </div>

        {!isTemplateLoading && (
          <CreateReportTemplateFiltersBlock
            key={id}
            initialValues={templateData?.result || null}
            selectedColumnsData={selectedColumnsData?.result}
            ref={filtersBlockRef}
            onValidationChange={setIsPreviewEnabled}
            onConfigChange={() => setSelectedColumn(undefined)}
            initialVisualization={initialVisualization}
          />
        )}
      </div>
    </div>
  );
};

export default CreateReportTemplatePage;
